import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  QrCode,
  Receipt,
  ArrowDownToLine,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  Zap,
  Gavel,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const ALL_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "view_dashboard", testid: "nav-dashboard" },
  { to: "/gerar-pix", label: "Gerar PIX", icon: QrCode, perm: "generate_pix", testid: "nav-gerar-pix" },
  { to: "/vendas", label: "Vendas", icon: Receipt, perm: "view_sales", testid: "nav-vendas" },
  { to: "/contestacoes", label: "MED", icon: Gavel, perm: "view_disputes", testid: "nav-disputes" },
  { to: "/saque", label: "Saque", icon: ArrowDownToLine, perm: "manage_withdrawals", testid: "nav-saque" },
  { to: "/configuracoes", label: "Config", icon: SettingsIcon, perm: null, testid: "nav-config" },
];

export default function AppLayout() {
  const { logout, user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = ALL_ITEMS.filter((i) => !i.perm || hasPerm(i.perm));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-64 flex-col border-r border-border bg-card/40 backdrop-blur-xl sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center pix-glow">
              <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground leading-none">MACHADO LIMA</p>
              <p className="text-sm font-display font-bold leading-tight">PIX Dashboard</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                )
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
              {user?.username?.[0]?.toUpperCase() || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">Logado como</p>
              <div className="text-sm font-medium truncate flex items-center gap-1">
                <span>{user?.username || "admin"}</span>
                {user?.is_admin && (
                  <Badge variant="outline" className="text-[9px] py-0 h-4 border-primary/40 text-primary bg-primary/10">
                    ADMIN
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 glass flex items-center justify-between px-4 h-14 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Zap className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-sm">ML PIX</span>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-secondary"
          data-testid="mobile-menu-open"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-0 right-0 bottom-0 w-72 bg-card border-l border-border p-6 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <span className="font-display font-bold">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-secondary"
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1">
              {items.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-${testid}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )
                  }
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="justify-start text-muted-foreground"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair ({user?.username})
            </Button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-border flex items-center justify-around h-16"
        data-testid="mobile-bottom-nav"
      >
        {items.slice(0, 5).map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`bottom-${testid}`}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-[10px] font-medium transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )
            }
          >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
